import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import { CommonLaboratoryResponse, CommonPetitionerResponse } from '@/api/services/common.dto'
import { CommonMostLikelyMorphologicCodeResponse } from '@/api/services/common/most-likely-morphologic-code/common-most-likely-morphologic-code.dto'
import { CommonProcedureResponse } from '@/api/services/common/procedure/common-procedure.dto'
import { CommonSampleTypeResponse } from '@/api/services/common/sample-type/common-sample-type.dto'
import { CommonStatusResponse } from '@/api/services/common/status/common-status.dto'
import { CommonTopographicCodeResponse } from '@/api/services/common/topographic-code/common-topographic-code.dto'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import i18n from '@/lang/i18n'
import { StatisticalAnalysisExternalListCase, StatisticalAnalysisExternalListColumnsCases } from '@/modules/statistical-analysis-module/shared/external-list-columns/external-list-columns-cases'
import { ColDef, ColGroupDef } from '@ag-grid-enterprise/all-modules'

export interface StatisticalAnalysisExternalListSampleFirstSampleGrossing {
    grossingDate: string
    responsibleForGrossing: string
    sgdId: number
}

export interface StatisticalAnalysisValuableConceptAppliedOfTypeUCL {
    sampleMicroUcl: string
    sampleMacroUcl: string
    totalSampleUcl:number
    valuableConceptId: number
    value: number
}

export interface StatisticalAnalysisValuableConceptAppliedOfTypeURV{
    totalSampleUrv: number,
    sampleUrv:string
    valuableConceptId: number,
    value: number
}

export interface StatisticalAnalysisVirtualShelf{
    id: number,
    name: string
}

export interface StatisticalAnalysisExternalListSample {
    caseStudy: StatisticalAnalysisExternalListCase
    firstSampleGrossing: StatisticalAnalysisExternalListSampleFirstSampleGrossing
    grossingCoordinator: CommonPetitionerResponse
    grossingTechController: CommonPetitionerResponse
    grossingTutor: CommonPetitionerResponse
    hasAttachments: boolean
    hasRevisions: boolean
    label: string
    mostLikelyMorphologicCode: CommonMostLikelyMorphologicCodeResponse
    procedure: CommonProcedureResponse
    regrossingLastDate: string
    responsibleRegrossing: CommonPetitionerResponse
    sampleId: number
    sampleType: CommonSampleTypeResponse
    samplingDate: string
    specialPreserve: boolean
    specialist: CommonPetitionerResponse
    status: CommonStatusResponse
    topographicCode: CommonTopographicCodeResponse
    totalUCL: number
    totalURV: number
    valuableConceptAppliedOfTypeUCL: StatisticalAnalysisValuableConceptAppliedOfTypeUCL
    valuableConceptAppliedOfTypeURV: StatisticalAnalysisValuableConceptAppliedOfTypeURV
    virtualShelf: StatisticalAnalysisVirtualShelf
    wasteDate: string
    wasteTechnician: CommonPetitionerResponse,
    laboratory: CommonLaboratoryResponse
}

export const StatisticalAnalysisExternalListColumnsSample = (): ExportedColDef[] => {
    return [
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.sampleNumber'),
            field: 'label',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.sampleType'),
            field: 'sampleType.description',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.topographicCode'),
            field: 'topographicCode.description',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.mostLikelyDiagnose'),
            field: 'mostLikelyMorphologicCode.description',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            hide: true,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.procedure'),
            field: 'procedure.description',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            hide: true,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.shelf'),
            field: 'virtualShelf.name',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            hide: true,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.COMMON_SAMPLE_STATUS,
            headerName: i18n.tc('global.status'),
            field: 'status',
            exportField: 'status.name',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.dateGrossingProcessed'),
            field: 'firstSampleGrossing.grossingDate',
            exportType: AgGridExportType.DATETIME,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.samplingDate'),
            field: 'samplingDate',
            hide: true,
            exportType: AgGridExportType.DATETIME,
            minWidth: 120,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.regrossingDate'),
            field: 'regrossingLastDate',
            hide: true,
            exportType: AgGridExportType.DATETIME,
            minWidth: 120,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.DATETIME,
            headerName: i18n.tc('statisticalAnalysis.grids.wasteDate'),
            field: 'wasteDate',
            hide: true,
            exportType: AgGridExportType.DATETIME,
            minWidth: 120,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.responsibleForGrossing'),
            field: 'firstSampleGrossing.responsibleForGrossing.fullName',
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.specialist'),
            field: 'specialist.fullName',
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.grossingTechController'),
            field: 'grossingTechController.fullName',
            exportType: AgGridExportType.TEXT,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.grossingCoordinator'),
            field: 'grossingCoordinator.fullName',
            exportType: AgGridExportType.TEXT,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.grossingTutor'),
            field: 'grossingTutor.fullName',
            exportType: AgGridExportType.TEXT,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.dossiers.serviceActivity.valueUnit'),
            field: 'valuableConceptAppliedOfTypeURV.totalSampleUrv',
            exportType: AgGridExportType.FLOAT,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.valueUnitTotal'),
            field: 'totalURV',
            exportType: AgGridExportType.DOUBLE,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.dossiers.serviceActivity.workUnit'),
            field: 'valuableConceptAppliedOfTypeUCL.totalSampleUcl',
            exportType: AgGridExportType.FLOAT,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.TEXT,
            headerName: i18n.tc('statisticalAnalysis.grids.workUnitTotal'),
            field: 'totalUCL',
            exportType: AgGridExportType.DOUBLE,
            hide: true,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.hasRevisions'),
            field: 'hasRevisions',
            exportType: AgGridExportType.BOOLEAN,
            sortable: false,
            filter: false,
            flex: 1,
            minWidth: 200,
            hide: true
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.specialPreserve'),
            field: 'specialPreserve',
            exportType: AgGridExportType.BOOLEAN,
            sortable: false,
            filter: false,
            flex: 1,
            minWidth: 200,
            hide: true
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.hasAttachments'),
            field: 'hasAttachments',
            exportType: AgGridExportType.BOOLEAN,
            sortable: false,
            filter: false,
            headerComponentParams: {
                template: buildIconHeaderTemplate(`far fa-paperclip`)
            },
            hide: true
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.hasIncidents'),
            field: 'hasIncidents',
            exportType: AgGridExportType.BOOLEAN,
            sortable: false,
            filter: false,
            headerComponentParams: {
                template: buildIconHeaderTemplate(`far fa-exclamation-triangle`)
            },
            hide: true
        },
        {
            type: AgGridColumnType.CHECK_ICON,
            headerName: i18n.tc('statisticalAnalysis.grids.sampleTag'),
            field: 'hasSampleTags',
            exportType: AgGridExportType.BOOLEAN,
            sortable: false,
            filter: false,
            flex: 1,
            minWidth: 200,
            hide: true
        }
    ]
}

export const StatisticalAnalysisExternalListSamples = (): (ColDef[] | ColGroupDef[] | ExportedColGroupDef[] | ExportedColDef []) => {
    return [
        {
            headerName: i18n.tc('statisticalAnalysis.grids.groupCases'),
            children: StatisticalAnalysisExternalListColumnsCases().map((item:ExportedColDef) => {
                item.field = `caseStudy.${item.field}`
                if (item.exportField) {
                    item.exportField = `caseStudy.${item.exportField}`
                }
                return item
            })
        },
        {
            headerName: i18n.tc('statisticalAnalysis.grids.groupSamples'),
            children: StatisticalAnalysisExternalListColumnsSample()
        }
    ]
}
